import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { GraduationCap, LogOut, ArrowRight, ArrowLeft, CheckCircle2, Award, BookOpen, Circle, Heart } from "lucide-react";
import portalApi, { portalStorage, formatPortalError } from "./portalApi";
import usePageMeta from "../../lib/usePageMeta";
import api from "../../lib/api";
import PreviewBanner from "./PreviewBanner";
import { previewStore, buildPreviewCourseBundle, PREVIEW_LEARNER } from "./previewStore";
import LearnerLiveClasses from "./LearnerLiveClasses";

function useLearner() {
  const nav = useNavigate();
  const [learner, setLearner] = useState(() => (previewStore.isActive() ? PREVIEW_LEARNER : portalStorage.getLearner()));
  useEffect(() => {
    if (previewStore.isActive()) { setLearner(PREVIEW_LEARNER); return; }
    if (!portalStorage.getToken()) { nav("/academy/portal/login"); return; }
    portalApi.get("/portal/me").then(r => { setLearner(r.data); portalStorage.setLearner(r.data); })
      .catch(()=>{});
  }, [nav]);
  return learner;
}

function PortalHeader({ learner }) {
  const nav = useNavigate();
  const inPreview = previewStore.isActive();
  const logout = () => {
    if (inPreview) { previewStore.exit(); nav("/care-portal/admin/academy-portal"); return; }
    portalStorage.clearToken(); portalStorage.clearLearner(); nav("/academy/portal");
  };
  return (
    <nav className="bg-white border-b border-slate-200 sticky top-0 z-40">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3.5 flex items-center justify-between">
        <Link to="/academy/portal/dashboard" className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[#4A6FA5] text-white flex items-center justify-center"><GraduationCap className="w-5 h-5" strokeWidth={1.75}/></div>
          <div>
            <div className="font-heading text-slate-900 text-sm font-semibold leading-tight">CCCHAPS</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 leading-tight">Chaplain Training Academy</div>
          </div>
        </Link>
        <div className="flex items-center gap-3 text-sm">
          {learner && <span className="text-slate-600 hidden sm:inline">Signed in as {learner.first_name}</span>}
          <button onClick={logout} data-testid="portal-logout" className="inline-flex items-center gap-1.5 text-slate-600 hover:text-[#4A6FA5] font-medium"><LogOut className="w-4 h-4"/> {inPreview ? "Exit Preview" : "Sign Out"}</button>
        </div>
      </div>
    </nav>
  );
}

// Build section list from a course record
export function buildSections(course) {
  const secs = [{ id: "welcome", label: "Welcome", type: "welcome" }];
  course.modules.forEach(m => secs.push({ id: m.id, label: `Module ${m.order}: ${m.title}`, type: "module", module: m }));
  secs.push({ id: "case-study", label: "Case Study", type: "case-study" });
  secs.push({ id: "knowledge-check", label: "Knowledge Check", type: "quiz" });
  secs.push({ id: "evaluation", label: "Course Evaluation", type: "evaluation" });
  secs.push({ id: "completion", label: "Completion", type: "completion" });
  return secs;
}

export function computeProgress(sections, progress, quizPassed, evaluation) {
  const completions = progress?.section_completions || {};
  let done = 0;
  const total = sections.length;
  sections.forEach(s => {
    if (s.type === "welcome" && completions.welcome) done++;
    else if (s.type === "module" && completions[s.id]) done++;
    else if (s.type === "case-study" && progress?.case_study_completed) done++;
    else if (s.type === "quiz" && quizPassed) done++;
    else if (s.type === "evaluation" && evaluation) done++;
    else if (s.type === "completion" && evaluation) done++;
  });
  return { done, total, pct: Math.round(done * 100 / total) };
}

export default function PortalDashboard() {
  usePageMeta({ title: "Your Academy Dashboard | CCCHAPS", description: "" });
  const learner = useLearner();
  const [data, setData] = useState(null);
  const [previewCourse, setPreviewCourse] = useState(null);
  const [hospEnroll, setHospEnroll] = useState(null);  // { course, enrollment, pct, modules_done, modules_total }
  const [hospBusy, setHospBusy] = useState(false);
  const inPreview = previewStore.isActive();

  // Load course + progress data. Route to admin preview endpoints in preview mode.
  const loadData = () => {
    if (inPreview) {
      // If we already have the course, just rebuild the bundle from preview state.
      if (previewCourse) {
        setData(buildPreviewCourseBundle(previewCourse));
        return Promise.resolve();
      }
      return api.get("/admin/portal/preview/course")
        .then(r => {
          setPreviewCourse(r.data.course);
          setData(buildPreviewCourseBundle(r.data.course));
        })
        .catch(err => {
          toast.error("Preview mode requires admin sign-in. Please sign in as an administrator first.");
          previewStore.exit();
        });
    }
    // Fetch CTA-001 (primary) + all enrollments to detect HOSP-001 state
    return Promise.all([
      portalApi.get("/portal/courses/CTA-001").then(r => setData(r.data)).catch(err => toast.error(formatPortalError(err))),
      portalApi.get("/portal/enrollments").then(r => {
        const hosp = (r.data || []).find(x => x?.course?.code === "HOSP-001");
        setHospEnroll(hosp || null);
      }).catch(() => {}),
    ]);
  };

  const enrollHosp = async () => {
    setHospBusy(true);
    try {
      await portalApi.post("/portal/enroll", { course_code: "HOSP-001" });
      await loadData();
      toast.success("You're enrolled in HOSP-001.");
    } catch (err) { toast.error(formatPortalError(err)); } finally { setHospBusy(false); }
  };

  useEffect(() => { if (learner) loadData(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [learner]);

  if (!learner || !data) return (
    <div className="min-h-screen bg-[#FDFCF6]">
      {inPreview && <PreviewBanner course={previewCourse} />}
      <PortalHeader learner={learner}/>
      <div className="p-12 text-center text-slate-500">Loading…</div>
    </div>
  );

  const sections = buildSections(data.course);
  const quizPassed = !!(data.best_quiz && data.best_quiz.passed);
  const { done, total, pct } = computeProgress(sections, data.progress, quizPassed, data.evaluation);
  const isCompleted = data.enrollment?.status === "completed";

  // Determine which section to resume at
  let resumeId = "welcome";
  const completions = data.progress?.section_completions || {};
  for (const s of sections) {
    if (s.type === "module" && !completions[s.id]) { resumeId = s.id; break; }
    if (s.type === "welcome" && !completions.welcome) { resumeId = "welcome"; break; }
    if (s.type === "case-study" && !data.progress?.case_study_completed) { resumeId = "case-study"; break; }
    if (s.type === "quiz" && !quizPassed) { resumeId = "knowledge-check"; break; }
    if (s.type === "evaluation" && !data.evaluation) { resumeId = "evaluation"; break; }
    if (s.type === "completion") { resumeId = "completion"; break; }
  }

  const ctaLabel = isCompleted ? "REVIEW COURSE" : (pct === 0 ? "BEGIN COURSE" : "CONTINUE COURSE");

  return (
    <div className="bg-[#FDFCF6] min-h-screen" data-testid="portal-dashboard">
      {inPreview && <PreviewBanner course={previewCourse} onSyncPreview={loadData} />}
      <PortalHeader learner={learner}/>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Learner Dashboard</p>
        <h1 className="mt-2 font-heading text-3xl sm:text-4xl tracking-tight text-slate-900">Welcome, {learner.first_name}.</h1>

        <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-sm">
          <div className="flex flex-wrap items-start gap-3 justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-[#4A6FA5] font-semibold">CTA-001</p>
              <h2 className="mt-1 font-heading text-2xl text-slate-900 leading-tight">Grief in the Workplace</h2>
              <p className="text-sm text-slate-500">What Every Chaplain Should Know · Approx. 60 min · Introductory</p>
            </div>
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold ${isCompleted ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-[#F0F4F8] text-[#4A6FA5] border border-slate-200"}`}>
              {isCompleted ? "COMPLETED" : "IN PROGRESS"}
            </span>
          </div>

          <div className="mt-6">
            <div className="flex items-center justify-between text-xs text-slate-500 mb-1.5">
              <span>Course progress</span><span data-testid="dashboard-progress-pct">{pct}%</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-[#4A6FA5] transition-all" style={{width: `${pct}%`}}/></div>
            <p className="mt-2 text-xs text-slate-500">{done} of {total} sections complete</p>
          </div>

          <dl className="mt-6 grid sm:grid-cols-2 gap-3 text-sm">
            <StatRow label="Enrollment" value={isCompleted ? "Completed" : "Active"}/>
            <StatRow label="Modules" value={`${Object.keys(completions).filter(k=>k.startsWith('module-')).length} of ${data.course.modules.length}`}/>
            <StatRow label="Case Study" value={data.progress?.case_study_completed ? "Complete" : "Pending"}/>
            <StatRow label="Knowledge Check" value={quizPassed ? `Passed (${data.best_quiz.score}/${data.best_quiz.total})` : (data.best_quiz ? `${data.best_quiz.score}/${data.best_quiz.total} — retry available` : "Not started")}/>
            <StatRow label="Evaluation" value={data.evaluation ? "Submitted" : "Pending"}/>
            <StatRow label="Certificate" value={data.certificate ? data.certificate.cert_number : "Pending"}/>
          </dl>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to={`/academy/portal/learn/${resumeId}`} data-testid="dashboard-primary-cta" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B]">
              {ctaLabel} <ArrowRight className="w-4 h-4"/>
            </Link>
            {data.certificate && (
              <Link to={`/academy/portal/certificate/${data.certificate.id}`} data-testid="dashboard-cert-cta" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-[#4A6FA5] text-[#4A6FA5] font-medium hover:bg-[#F0F4F8]">
                <Award className="w-4 h-4"/> VIEW CERTIFICATE
              </Link>
            )}
          </div>
        </div>

        <p className="mt-6 text-[11px] text-slate-500 max-w-3xl leading-relaxed">
          CTA-001 is introductory educational training. Completion does not confer professional licensure, clinical certification, accredited continuing-education credit, or authority to provide mental-health diagnosis or clinical treatment.
        </p>

        {!inPreview && (
          <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-sm" data-testid="dashboard-hosp-card">
            <div className="flex flex-wrap items-start gap-3 justify-between">
              <div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] font-semibold text-amber-800 mb-2">Free Track Three Preview</span>
                <p className="text-xs uppercase tracking-[0.18em] text-[#4A6FA5] font-semibold">HOSP-001</p>
                <h2 className="mt-1 font-heading text-2xl text-slate-900 leading-tight">See the Person First</h2>
                <p className="text-sm text-slate-500">An Introduction to Patient-Centered Hospice Chaplaincy · Approx. 35–45 min · Introductory</p>
              </div>
              <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold ${hospEnroll?.enrollment?.status === "completed" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : hospEnroll ? "bg-[#F0F4F8] text-[#4A6FA5] border border-slate-200" : "bg-slate-50 text-slate-600 border border-slate-200"}`}>
                {hospEnroll?.enrollment?.status === "completed" ? "COMPLETED" : hospEnroll ? "IN PROGRESS" : "NOT ENROLLED"}
              </span>
            </div>

            {hospEnroll && (
              <div className="mt-6">
                <div className="flex items-center justify-between text-xs text-slate-500 mb-1.5">
                  <span>Course progress</span><span data-testid="dashboard-hosp-pct">{hospEnroll.pct}%</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-[#4A6FA5] transition-all" style={{width: `${hospEnroll.pct}%`}}/></div>
                <p className="mt-2 text-xs text-slate-500">{hospEnroll.modules_done} of {hospEnroll.modules_total} lessons complete</p>
              </div>
            )}

            <div className="mt-6 flex flex-wrap gap-3">
              {!hospEnroll ? (
                <button onClick={enrollHosp} disabled={hospBusy} data-testid="dashboard-hosp-start" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B] disabled:opacity-60">
                  {hospBusy ? "Enrolling…" : "Start Course"} <ArrowRight className="w-4 h-4"/>
                </button>
              ) : (
                <Link to="/academy/portal/hosp-001/learn" data-testid="dashboard-hosp-continue" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B]">
                  {hospEnroll.enrollment.status === "completed" ? "Review Course" : (hospEnroll.pct > 0 ? "Continue Course" : "Start Course")} <ArrowRight className="w-4 h-4"/>
                </Link>
              )}
              <Link to="/academy/hospice-chaplaincy" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-slate-300 text-slate-800 font-medium hover:border-[#4A6FA5] hover:text-[#4A6FA5]">
                <Heart className="w-4 h-4"/> Explore Track Three
              </Link>
            </div>
            <p className="mt-6 text-[11px] text-slate-500 leading-relaxed">
              HOSP-001 is introductory educational training. Completion does not confer professional licensure, clinical certification, accredited continuing-education credit, hospice certification, or authority to provide medical, mental-health, or clinical treatment.
            </p>
          </div>
        )}

        {!inPreview && hospEnroll && (
          <LearnerLiveClasses courseCode="HOSP-001" title="From the Book to the Bedside — Live" testId="dashboard-live-hosp" />
        )}

        <LearnerLiveClasses courseCode="CTA-001" title="CTA-001 · Live Classes" testId="live-classes-cta" />
      </div>
    </div>
  );
}

function StatRow({ label, value }) {
  return (
    <div className="flex justify-between gap-4 rounded-lg bg-[#F8FAFC] px-3 py-2 border border-slate-100">
      <dt className="text-slate-500">{label}</dt><dd className="font-medium text-slate-900 text-right">{value}</dd>
    </div>
  );
}

export { PortalHeader, useLearner };
